import { ConfirmationDialog } from '@/components/patterns/confirmation-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useIsReadOnly } from '@/lib/casl'
import { useFormContext } from '@/lib/form-contexts'
import { cn, formatCurrency } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import { CircleX } from 'lucide-react'
import DeliveryCutoffOverrideField from '../../DeliveryCutoffOverrideField'
import {
  BASE_DELIVERY_AREA_COLOR,
  NON_DELIVERY_AREA_COLOR,
  SPECIALTY_LOCATION_COLOR,
  getSemanticPolygonColor,
} from '../../DeliveryAreaMap/constants'

/**
 * Get color for an area based on its type and index
 */
const getAreaColor = (area, areaType) => {
  if (areaType === 'base') {
    return getSemanticPolygonColor(area?.polygon_type, area?.color || BASE_DELIVERY_AREA_COLOR)
  }
  if (areaType === 'location') {
    return SPECIALTY_LOCATION_COLOR
  }
  if (areaType === 'excluded') {
    return NON_DELIVERY_AREA_COLOR
  }
  return area?.color || BASE_DELIVERY_AREA_COLOR
}

export const DeliveryAreaList = ({
  areas,
  areaType, // 'base' | 'location' | 'excluded'
  selectedIndex,
  onSelect,
  onDelete,
  validationErrors = {},
  canDelete = true,
  getGlobalIndex,
  isDetecting = false,
}) => {
  const form = useFormContext()
  const { isReadOnly } = useIsReadOnly()
  const hasCutoffOverrides = useStore(
    form.store,
    (state) => state.values.is_delivery_cutoff_overrides_enabled === true
  )
  const FIELD_NAME = 'delivery_area_fees'
  if (!areas || areas.length === 0) {
    return null
  }

  // Get placeholder text for name input based on area type
  const getNamePlaceholder = (areaType) => {
    if (areaType === 'location') return 'Special Price Place'
    if (areaType === 'excluded') return 'No Delivery Area'
    return 'Name'
  }

  const getAreaLabel = (areaType, index) => {
    if (areaType === 'base') return 'Delivery boundary'
    if (areaType === 'excluded') return `No Delivery Area ${index + 1}`
    return `Special Price Place ${index + 1}`
  }

  const getDeleteTitle = (areaType) => {
    if (areaType === 'base') return 'Delivery Boundary'
    if (areaType === 'excluded') return 'No Delivery Area'
    return 'Special Price Place'
  }

  const getDeleteDescription = (areaType) => {
    if (areaType === 'base') return 'base delivery area'
    if (areaType === 'excluded') return 'no-delivery area'
    return 'special price place'
  }

  return (
    <div className="space-y-1.5">
      {areas.map((area, localIndex) => {
        const color = getAreaColor(area, areaType)
        const isSelected = selectedIndex === localIndex
        const hasError = validationErrors[localIndex]
        const isInactive = area.is_active === false
        const globalIndex = getGlobalIndex ? getGlobalIndex(area, localIndex) : localIndex

        return (
          <div
            key={area.id || area.reference_id || `${areaType}-${localIndex}`}
            className={cn(
              'p-1.5 border rounded-md cursor-pointer transition-all',
              isSelected && 'border-primary bg-primary/5 ring-1 ring-primary/20',
              hasError && 'border-status-destructive-border bg-status-destructive-background',
              isInactive && 'bg-muted/30 text-muted-foreground',
              !isSelected && !hasError && 'hover:border-muted-foreground/50'
            )}
            onClick={() => onSelect(localIndex)}
          >
            {isDetecting && areaType === 'base' ? (
              <Skeleton className="h-7 w-full" />
            ) : (
              <div className="flex items-center gap-1.5">
                <div
                  className="w-4 h-4 rounded flex-shrink-0 ring-1 ring-black/10 shadow-sm"
                  style={{ backgroundColor: color }}
                />

                {areaType === 'base' ? (
                  <div className="flex min-h-7 flex-1 items-center rounded-md px-1.5">
                    <span className="truncate text-sm font-medium text-foreground">
                      Delivery boundary
                    </span>
                  </div>
                ) : (
                  <div className="flex-1 min-w-0">
                    {isReadOnly ? (
                      <Input
                        value={area.name || getAreaLabel(areaType, localIndex)}
                        disabled
                        size="xs"
                        aria-invalid={hasError || undefined}
                      />
                    ) : (
                      <form.AppField name={`${FIELD_NAME}[${globalIndex}].name`}>
                        {(field) => (
                          <Input
                            value={field.state.value || ''}
                            onChange={(e) => {
                              e.stopPropagation()
                              field.handleChange(e.target.value)
                            }}
                            onBlur={field.handleBlur}
                            onClick={(e) => e.stopPropagation()}
                            placeholder={getNamePlaceholder(areaType)}
                            size="xs"
                            aria-invalid={hasError || undefined}
                          />
                        )}
                      </form.AppField>
                    )}
                  </div>
                )}

                {areaType === 'excluded' && (
                  <Badge
                    variant="destructiveSoft"
                    size="control-sm"
                    shape="control"
                    className="flex-1 justify-start"
                  >
                    No delivery
                  </Badge>
                )}

                {areaType !== 'excluded' && (
                  <div className="flex-1 min-w-0">
                    {isReadOnly ? (
                      <Input
                        value={area.fee == null ? 'No fallback fee' : formatCurrency(area.fee)}
                        disabled
                        size="xs"
                        aria-invalid={hasError || undefined}
                      />
                    ) : (
                      <form.AppField name={`${FIELD_NAME}[${globalIndex}].fee`}>
                        {(field) => (
                          <field.MoneyInput
                            placeholder={areaType === 'base' ? 'No fallback fee' : '0.00'}
                            formatEmptyAsZero={areaType !== 'base'}
                            showFreeBadge={
                              areaType !== 'base' ||
                              (field.state.value !== null &&
                                field.state.value !== undefined &&
                                field.state.value !== '')
                            }
                            aria-label={
                              areaType === 'base'
                                ? 'Boundary fallback delivery fee'
                                : `${getAreaLabel(areaType, localIndex)} delivery fee`
                            }
                            onClick={(e) => e.stopPropagation()}
                            size="xs"
                            aria-invalid={hasError || undefined}
                          />
                        )}
                      </form.AppField>
                    )}
                  </div>
                )}

                {areaType === 'location' && hasCutoffOverrides ? (
                  <div className="min-w-36 flex-[1.5]" onClick={(event) => event.stopPropagation()}>
                    <DeliveryCutoffOverrideField
                      ariaLabel={`${getAreaLabel(areaType, localIndex)} delivery cutoff`}
                      name={`${FIELD_NAME}[${globalIndex}].delivery_cutoff`}
                      size="xs"
                    />
                  </div>
                ) : null}

                {/* Delete button */}
                {!isReadOnly && (
                  <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
                    {!canDelete ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <Button
                              type="button"
                              variant="ghostMuted"
                              size="icon-2xs"
                              disabled={true}
                            >
                              <CircleX className="h-3.5 w-3.5" />
                            </Button>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {areaType === 'base'
                              ? 'Turn off or delete special price places and no-delivery places first'
                              : 'Cannot delete'}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <ConfirmationDialog
                        title={`Delete ${getDeleteTitle(areaType)}`}
                        variant="destructive"
                        noButtonText
                        description={`Are you sure you want to delete this ${getDeleteDescription(areaType)}? This action cannot be undone.`}
                        onConfirm={() => onDelete(localIndex)}
                        trigger={
                          <Button type="button" variant="ghostDestructive" size="icon-2xs">
                            <CircleX className="h-3.5 w-3.5" />
                          </Button>
                        }
                      />
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Validation error - shown below the row */}
            {hasError && (
              <div className="ml-5">
                <Typography variant="caption" tone="destructive" italic>
                  {hasError}
                </Typography>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
